/**
 * The chat lists as they were last fetched, kept for the lifetime of the tab.
 *
 * The Communication page is a route, so anything that navigates away from it -- opening
 * someone's profile from a message, above all -- unmounts it and throws the lists away.
 * Coming back refetched all three from Supabase, and since a thread can only be selected
 * once its row is on hand, the user watched the chat list appear and the conversation
 * arrive after it. That read as two redirects.
 *
 * Seeding state from here makes a remount render what was already on screen on its first
 * paint, and the fetch that follows is quiet: it swaps the rows in when it answers. This
 * is a cache in the weak sense -- it is never the source of truth, nothing expires, and a
 * miss is simply the old behaviour.
 *
 * Keys carry the viewer (and for departments, the event), so one account's rows can never
 * be shown to another. `clearChatListCache` runs on sign-out anyway, to free the memory
 * and to be sure.
 */

const cache = new Map<string, readonly unknown[]>();

/** Rows cached under `key`, or null for a miss. A null key never hits. */
export function cachedRows<T>(key: string | null): T[] | null {
  if (!key) return null;
  const hit = cache.get(key);
  // The array is handed back by reference on purpose: setting state to the very same
  // array React already holds is a no-op, so a quiet refetch that changes nothing costs
  // no render.
  return hit ? (hit as T[]) : null;
}

/** Remember `rows` under `key`. Only ever called with a successful fetch's result. */
export function cacheRows<T>(key: string | null, rows: T[]): void {
  if (!key) return;
  cache.set(key, rows);
}

/** Drop everything. Called on sign-out. */
export function clearChatListCache(): void {
  cache.clear();
}
